import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComments,
  faClock,
  faSearch,
  faPlay,
  faExclamationTriangle,
  faSpinner,
  faArrowRight,
  faSitemap,
  faCoins,
  faTags,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { formatDistanceToNow } from 'date-fns';
import Cookies from 'js-cookie';
import MainLayout from '../Layout/MainLayout';
import { useAuth } from '../../context/AuthContext';

const EnhancedThreadsList = () => {
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('workflow'); // 'workflow' or 'regular'
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const agentId = searchParams.get('agentId'); // Get agentId from URL for back navigation
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Handle back navigation
  const handleBack = () => {
    if (agentId) {
      navigate(`/agents/${agentId}`);
    } else {
      navigate('/agents');
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    filterAndSortSessions();
  }, [sessions, searchTerm, activeTab]);

  const loadSessions = async () => {
    try {
      const token = Cookies.get('sankalvax_token');
      const response = await fetch('/api/chat/sessions?limit=100', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      console.log('📋 Loaded sessions:', data.sessions?.length, 'total');
      console.log('📋 Workflow sessions:', data.sessions?.filter(s => s.is_workflow_chat).length);
      console.log('📋 Draft sessions:', data.sessions?.filter(s => s.session_status === 'draft'));
      setSessions(data.sessions || []);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortSessions = () => {
    let filtered = sessions;

    // Filter by chat type
    if (activeTab === 'workflow') {
      filtered = filtered.filter(session => session.is_workflow_chat);
    } else {
      filtered = filtered.filter(session => !session.is_workflow_chat);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(session =>
        (session.session_name && session.session_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (session.workflow_name && session.workflow_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Sort by created_at (newest first) - keeps order stable when workflows complete
    filtered = filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    setFilteredSessions(filtered);
  };

  const continueChat = (session) => {
    if (session.is_workflow_chat && session.workflow_id) {
      // Workflow chat - redirect to SankalvaX Chat with workflow context (V2 by default)
      navigate(`/chat/${session.session_id}?workflow=${session.workflow_id}&workflowName=${encodeURIComponent(session.workflow_name || 'Workflow')}&mode=workflow&version=v2`);
    } else {
      // Regular chat - redirect to SankalvaX Chat with session ID
      navigate(`/chat/${session.session_id}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    return `${Math.floor(seconds / 60)}m ${Math.floor(seconds % 60)}s`;
  };

  const workflowSessions = sessions.filter(s => s.is_workflow_chat);
  const regularSessions = sessions.filter(s => !s.is_workflow_chat);

  if (loading) {
    return (
      <MainLayout title="Bill Processing History">
        <div className="flex justify-center items-center h-96">
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} className="text-4xl text-blue-500 animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading your conversations...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Bill Processing History">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        
        {/* Back Button */}
        <div className="flex items-center mb-4">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-600 dark:text-gray-400"
            title={agentId ? "Back to Agent" : "Back to Agents"}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            <span className="text-sm">{agentId ? "Back to Agent" : "Back to Agents"}</span>
          </button>
        </div>

        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 rounded-3xl mb-6 shadow-xl">
            <FontAwesomeIcon icon={faComments} className="text-white text-2xl" />
          </div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-4">
            Your Conversations
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-xl max-w-2xl mx-auto">
            Continue your AI conversations and review workflow executions with enhanced interactive interface
          </p>
        </div>

        {/* Tabs Navigation */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
            <button
              onClick={() => setActiveTab('workflow')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeTab === 'workflow'
                  ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <FontAwesomeIcon icon={faSitemap} className="text-sm" />
              <span>Workflow Chats</span>
              <span className="ml-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-xs rounded-full">
                {workflowSessions.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('regular')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeTab === 'regular'
                  ? 'bg-white dark:bg-gray-700 text-green-600 dark:text-green-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <FontAwesomeIcon icon={faComments} className="text-sm" />
              <span>Regular Chats</span>
              <span className="ml-1 px-2 py-0.5 bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 text-xs rounded-full">
                {regularSessions.length}
              </span>
            </button>
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-80">
            <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={`Search ${activeTab} chats...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
            />
          </div>
        </div>

        {/* Sessions Grid */}
        {filteredSessions.length === 0 ? (
          <div className="text-center py-16">
            <FontAwesomeIcon 
              icon={activeTab === 'workflow' ? faSitemap : faComments} 
              className="text-6xl text-gray-300 dark:text-gray-600 mb-6" 
            />
            <h3 className="text-2xl font-semibold text-gray-500 dark:text-gray-400 mb-3">
              {searchTerm ? 'No matches found' : `No ${activeTab} chats yet`}
            </h3>
            <p className="text-gray-400 dark:text-gray-500 mb-6">
              {searchTerm 
                ? 'Try adjusting your search terms'
                : `Start a new ${activeTab === 'workflow' ? 'workflow' : 'conversation'} to see it here`
              }
            </p>
            {!searchTerm && isAdmin && (
              <button
                onClick={() => navigate(activeTab === 'workflow' ? '/workflows' : '/chat-playground')}
                className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
              >
                <FontAwesomeIcon icon={faPlay} className="mr-2" />
                Start New {activeTab === 'workflow' ? 'Workflow' : 'Chat'}
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-6">
            {filteredSessions.map((session) => (
              <div
                key={session.session_id}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl hover:border-blue-200 dark:hover:border-blue-600 transition-all duration-300 overflow-hidden group"
              >
                <div className="p-8">
                  <div className="flex items-start justify-between">

                    {/* Left: Main Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start space-x-6">

                        {/* Icon */}
                        <div className={`flex-shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg ${
                          session.is_workflow_chat
                            ? 'bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600'
                            : 'bg-gradient-to-br from-green-500 via-teal-500 to-cyan-600'
                        }`}>
                          <FontAwesomeIcon
                            icon={session.is_workflow_chat ? faSitemap : faComments} 
                            className="text-white text-lg" 
                          />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          
                          {/* Title and Status */}
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                              {session.session_name || session.workflow_name || `Workflow ${session.workflow_id}`}
                            </h3>
                            {/* Bill Amount Badge */}
                            {session.bill_total_amount && session.bill_total_amount !== 'null' && !isNaN(parseFloat(session.bill_total_amount)) && (
                              <div className="flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-bold bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700 shadow-sm">
                                <FontAwesomeIcon icon={faCoins} className="text-xs" />
                                <span>
                                  {(session.bill_currency && session.bill_currency !== 'null' && session.bill_currency !== 'None') ? session.bill_currency : '$'}{' '}
                                  {parseFloat(session.bill_total_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </div>
                            )}
                            {session.session_status === 'draft' && (
                              <div className="flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-yellow-100 to-orange-100 dark:from-yellow-900/30 dark:to-orange-900/30 text-yellow-700 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-700 shadow-sm">
                                <FontAwesomeIcon icon={faClock} />
                                <span className="font-semibold">Draft</span>
                              </div>
                            )}
                          </div>

                          {/* Metadata */}
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-3">
                            <div className="flex items-center space-x-1">
                              <FontAwesomeIcon icon={faClock} />
                              <span>{formatDate(session.updated_at || session.created_at)}</span>
                            </div>
                            {session.total_tokens_used > 0 && (
                              <div className="flex items-center space-x-1">
                                <FontAwesomeIcon icon={faTags} />
                                <span>{session.total_tokens_used.toLocaleString()} tokens</span>
                              </div>
                            )}
                            {session.total_cost > 0 && (
                              <div className="flex items-center space-x-1">
                                <FontAwesomeIcon icon={faCoins} />
                                <span>${session.total_cost.toFixed(4)}</span>
                              </div>
                            )}
                            {session.latest_execution?.duration_seconds && (
                              <div className="flex items-center space-x-1">
                                <FontAwesomeIcon icon={faClock} />
                                <span>{formatDuration(session.latest_execution.duration_seconds)}</span>
                              </div>
                            )}
                          </div>

                          {/* Error message only - no step info */}
                          {session.is_workflow_chat && session.latest_execution && session.latest_execution.error_message && (
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                              <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                                {session.latest_execution.error_message.substring(0, 100)}...
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right: Action Buttons */}
                    <div className="flex-shrink-0 ml-6">
                      <div className="flex items-center space-x-3">

                        {/* Continue Chat Button */}
                        <button
                          onClick={() => continueChat(session)}
                          className="flex items-center space-x-3 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transform hover:scale-105 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl"
                        >
                          <FontAwesomeIcon icon={faArrowRight} className="text-sm" />
                          <span className="hidden sm:inline">Continue Chat</span>
                        </button>

                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer Stats */}
        <div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {workflowSessions.length}
              </div>
              <div className="text-gray-600 dark:text-gray-400">Workflow Chats</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                {regularSessions.length}
              </div>
              <div className="text-gray-600 dark:text-gray-400">Regular Chats</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                {sessions.reduce((sum, s) => sum + (s.total_tokens_used || 0), 0).toLocaleString()}
              </div>
              <div className="text-gray-600 dark:text-gray-400">Total Tokens</div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EnhancedThreadsList;